import { useQuery } from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import type { ApPage, ApVendor } from "@/accounts-payable/types";
import { flattenPages } from "@/accounts-payable/utils";
import { useApi } from "@/core/api";
import { vendorsKey } from "./keys";
import {
  type ApInvoiceListFilters,
  invoiceListParams,
  normalizeInvoiceFilters,
} from "./list-params";
import { AP_STALE_TIME } from "./polling";

/**
 * The invoices these filters match as a CSV the API streams, one row per
 * invoice or per line item. `onProgress` gets the bytes received so far: the
 * API can't say how many rows are coming. A 404 means nothing matched.
 */
export const downloadInvoiceExport = (
  api: AxiosInstance,
  businessId: string,
  kind: "invoices" | "line_items",
  filters: ApInvoiceListFilters,
  {
    signal,
    onProgress,
  }: { signal?: AbortSignal; onProgress?: (bytes: number) => void } = {},
): Promise<Blob> =>
  api
    .get<Blob>(`/businesses/${businessId}/ap/invoices/export`, {
      params: { kind, ...invoiceListParams(normalizeInvoiceFilters(filters)) },
      responseType: "blob",
      signal,
      onDownloadProgress: (event) => onProgress?.(event.loaded),
    })
    .then((response) => response.data);

/** Every vendor, for the export's vendor choice (the Vendors tab pages). */
export const useGetAllVendors = (businessId: string | null) => {
  const api = useApi();
  return useQuery({
    queryKey: [...vendorsKey(businessId), "all"] as const,
    queryFn: async ({ signal }) => {
      const pages: ApPage<ApVendor>[] = [];
      let page: number | null = 1;
      while (page !== null) {
        const { data }: { data: ApPage<ApVendor> } = await api.get<
          ApPage<ApVendor>
        >(`/businesses/${businessId}/ap/vendors`, {
          params: { page, page_size: 250 },
          signal,
        });
        pages.push(data);
        page = data.has_more ? data.next_page : null;
      }
      return flattenPages(pages);
    },
    enabled: !!businessId,
    staleTime: AP_STALE_TIME,
  });
};
